import React, { useState, FormEvent, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast as sonnerToast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import { getFriendlyAuthError } from '@/lib/authErrors';
import AppLogo from '@/assets/logo.svg?react';

const AuthForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn, signUp, signInWithGoogle, isAuthActionInProgress } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isAuthActionInProgress) return;
    const searchParams = new URLSearchParams(location.search);
    const referralCode = searchParams.get('ref');
    try {
      if (isSignUp) {
        await signUp(email, password, referralCode);
        sonnerToast.success('Account created!');
      } else {
        await signIn(email, password);
        sonnerToast.success('Welcome back.');
      }
      const rawRedirect = searchParams.get('redirect') || searchParams.get('returnUrl');
      const redirectPath =
        rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
          ? rawRedirect
          : '/studio';
      navigate(redirectPath);
    } catch (err: any) {
      if (err?.code === 'functions/unauthenticated') {
        sonnerToast.error('Security verification failed. Please refresh and try again.');
      } else {
        sonnerToast.error(getFriendlyAuthError(err));
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f5ef] text-slate-900 lg:flex">
      <div className="relative hidden w-1/2 overflow-hidden bg-slate-950 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_88%,rgba(16,185,129,0.14),transparent_38%),radial-gradient(circle_at_72%_22%,rgba(212,175,55,0.14),transparent_42%),linear-gradient(180deg,#020617_0%,#081331_100%)]" />
        <div className="relative z-10 p-10 xl:p-14">
          <div className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-3 py-2 backdrop-blur-sm">
            <span className="grid h-8 w-8 place-content-center rounded-xl bg-white shadow-sm ring-1 ring-black/5">
              <AppLogo className="h-[18px] w-[18px]" />
            </span>
            <span className="font-sans text-xl font-semibold tracking-[-0.03em] text-white">LendLifted</span>
          </div>
        </div>

        <div className="relative z-10 px-10 pb-14 xl:px-14">
          <div className="max-w-xl">
            <p className="mb-4 inline-flex rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-200">
              Seller Financing Platform
            </p>
            <h2 className="max-w-[12ch] text-5xl font-bold leading-[0.95] tracking-[-0.04em] text-white xl:text-6xl">
              Close more deals with{' '}
              <span className="text-amber-200">instant financing.</span>
            </h2>
            <p className="mt-6 max-w-[33rem] text-lg leading-[1.7] tracking-[-0.01em] text-slate-300">
              Empower your customers to say yes. Stripe Connect integration, instant quotes, and seamless checkout for high-ticket service providers.
            </p>
          </div>
          <div className="mt-16 text-sm text-slate-400/90">© {new Date().getFullYear()} LendLifted Platform</div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-5 sm:p-8 lg:w-1/2">
        <div className="w-full max-w-lg">
          <div className="mb-5 flex items-center justify-center lg:hidden">
            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
              <span className="grid h-8 w-8 place-content-center rounded-xl bg-slate-950 shadow-sm ring-1 ring-slate-900/10">
                <AppLogo className="h-[18px] w-[18px]" />
              </span>
              <span className="font-sans text-lg font-semibold tracking-[-0.03em] text-slate-900">LendLifted</span>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200/80 bg-white/92 p-6 shadow-[0_24px_60px_-38px_rgba(15,23,42,0.35)] backdrop-blur-sm sm:p-8">
            <div className="mb-8">
              <p className="mb-4 inline-flex rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-slate-600">
                Powered by Stripe Connect
              </p>
              <h1 className="text-3xl font-bold tracking-[-0.04em] text-slate-950 sm:text-[2rem]">
                {isSignUp ? 'Create your seller account' : 'Welcome back'}
              </h1>
              <p className="mt-3 max-w-md text-[0.98rem] leading-7 tracking-[-0.01em] text-slate-600">
                {isSignUp ? 'Start offering financing in minutes.' : 'Sign in to manage your quotes and settings.'}
              </p>
            </div>

            <div className="space-y-6">
              <Button
                variant="outline"
                onClick={() => {
                  const referralCode = new URLSearchParams(location.search).get('ref');
                  void signInWithGoogle(referralCode);
                }}
                disabled={isAuthActionInProgress}
                className="h-12 w-full rounded-xl border-slate-200 bg-white text-base font-medium text-slate-900 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-300"
              >
                Continue with Google
              </Button>

              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                <span className="relative bg-white px-4 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">
                  Or continue with email
                </span>
              </div>

              <form onSubmit={handleSubmit} ref={formRef} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-800">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-800">Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-300"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isAuthActionInProgress}
                  className="h-12 w-full rounded-xl bg-slate-900 text-base font-semibold text-white shadow-[0_14px_24px_-18px_rgba(15,23,42,0.9)] hover:bg-slate-800 focus-visible:ring-slate-400 active:scale-[0.99]"
                >
                  {isAuthActionInProgress ? <Loader2 className="animate-spin" /> : (isSignUp ? 'Sign Up' : 'Log In')} <ArrowRight className="ml-2 h-4 w-4"/>
                </Button>
              </form>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
              >
                {isSignUp ? 'Already have an account? Log In' : "Don't have an account? Sign Up"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
